import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { Table, Space, DatePicker, Button, Input } from 'antd'
import frLocale from 'antd/es/date-picker/locale/fr_FR'
import { SearchOutlined } from '@ant-design/icons'
import type { TablePaginationConfig } from 'antd'
import { FreeAnswer } from 'globals/models/FreeAnswer'
import { Pagination } from 'globals/models/Mail'
import UpsertFreeAnswerModal from './UpsertFreeAnswerModal'
import { Address } from 'globals/models/User'

interface Filter {
  field: string
  values: string[]
}

interface Props {
  currentCenters: Address[]
  freeAnswers: FreeAnswer[]
  pagination: Pagination
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, any>
  ) => void
  filters: Filter[]
}

const Tables: React.FC<Props> = ({
  currentCenters,
  freeAnswers,
  pagination,
  onTableChange,
  filters
}) => {
  const { t } = useTranslation()
  const { RangePicker } = DatePicker

  const [selectedFreeAnswer, setSelectedFreeAnswer] = useState<FreeAnswer | undefined>()

  const dataSource = freeAnswers.map((freeAnswer) => (
    {
      key: freeAnswer.id,
      forwardedAt: moment(freeAnswer.forwardedAt).format('DD/MM/yyyy'),
      createdAt: moment(freeAnswer.createdAt).format('DD/MM/yyyy'),
      trackingNumber: freeAnswer.trackingNumber,
      center: freeAnswer.addressName
    }
  ))

  const forwardedAtValues: string[] | undefined = filters.find(
    (el) => el.field === 'forwarded_at'
  )?.values
  const createdAtValues: string[] | undefined = filters.find(
    (el) => el.field === 'created_at'
  )?.values

  const columns = [
    {
      title: t('global.forwarded-at'),
      dataIndex: 'forwardedAt',
      key: 'forwardedAt',
      filterDropdown: ({
        setSelectedKeys,
        confirm,
        clearFilters
      }: any) => (
        <div style={{ padding: 8 }}>
          <Space>
            <RangePicker
              locale={frLocale}
              format={"DD/MM/YYYY"}
              onChange={(e) => setSelectedKeys([e])}
              allowClear={true}
              defaultValue={forwardedAtValues ? [
                moment(forwardedAtValues[0]),
                moment(forwardedAtValues[1])
              ] : undefined}
            />
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filteredValue: filters.find((el) => el.field === 'forwarded_at')?.values
    },
    {
      title: t('global.created-at'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      filterDropdown: ({
        setSelectedKeys,
        confirm,
        clearFilters
      }: any) => (
        <div style={{ padding: 8 }}>
          <Space>
            <RangePicker
              locale={frLocale}
              format={"DD/MM/YYYY"}
              onChange={(e) => setSelectedKeys([e])}
              allowClear={true}
              defaultValue={createdAtValues ? [
                moment(createdAtValues[0]),
                moment(createdAtValues[1])
              ] : undefined}
            />
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filteredValue: filters.find((el) => el.field === 'created_at')?.values
    },
    {
      title: t('global.tracking-number'),
      dataIndex: 'trackingNumber',
      key: 'trackingNumber',
      filterDropdown: ({
        setSelectedKeys,
        confirm,
        clearFilters
      }: any) => (
        <div style={{ padding: 8 }}>
          <Space>
            <Input
              onChange={(e) => setSelectedKeys([e.target.value])}
              allowClear={true}
              defaultValue={
                filters.find((el) => el.field === 'tracking_number')?.values[0]
              }
            />
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filteredValue: filters.find((el) => el.field === 'tracking_number')?.values
    },
    {
      title: t('global.center'),
      dataIndex: 'center',
      key: 'center'
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'x',
      render: (record: { key: string }) =>
        dataSource.length >= 1 ? (
          <a
            onClick={
              () => setSelectedFreeAnswer(
                freeAnswers.find(
                  (freeAnswer: FreeAnswer) => freeAnswer.id === record.key
                )
              )
            }
          >
            {t('global.modify')}
          </a>
        ) : null
    }
  ]

  return (
    <>
      <UpsertFreeAnswerModal
        createModalOpened={!!selectedFreeAnswer}
        setCreateModalOpened={(_) => setSelectedFreeAnswer(undefined)}
        currentCenters={currentCenters}
        freeAnswer={selectedFreeAnswer}
      />
      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.pageLength,
            total: pagination.totalItemsCount,
            showSizeChanger: true
          }}
          onChange={onTableChange}
        />
      </Space>
    </>
  )
}

export default Tables
